import { apiSlice } from './apiSlice';
import type {
  Planting,
  PlantingLifecycleStatus,
  PlantingPayload,
  PlantingQueryParams,
  PlantingUpdatePayload,
} from '../models/Planting';
import { adaptBbchInfo, adaptPlanting } from '../models/Planting';
import type { SaveWorld, WorldPlanting, WorldRuntimePlanting } from '../types/saves';
import { loadSaveWorldSnapshot, toQueryFnError } from './saveWorldQuery';
import i18n from '../i18n';

export type PlantingDispositionKind = 'partial_writeoff' | 'written_off' | 'partial_sale' | 'sold';

export interface PlantingDispositionPayload {
  kind: PlantingDispositionKind;
  quantityAffected?: number;
  effectiveDate: string;
  reason: string;
}

export interface PlantingDispositionResult {
  eventId: string;
  plantingId: string;
  quantityAffected: number;
  remainingQuantity: number;
  lifecycleStatus: string;
}

export type HarvestQuantityReplayPolicy =
  | 'hard_fact'
  | 'replayable_model_estimate'
  | 'replayable_event_value'
  | null;

export type HarvestQuantityOrigin =
  | 'event_property'
  | 'harvest_record'
  | 'model_estimate'
  | null;

export interface PlantingHarvestRecord {
  id: string;
  eventId: string | null;
  harvestedAt: string | null;
  quantityKg: number | null;
  marketableKg: number | null;
  saveStorageId: string | null;
  storageBedId: string | null;
  notes: string | null;
  createdAt: string | null;
}

export interface PlantingRemovalEvent {
  id: string;
  type: string;
  templateCode: string | null;
  eventDate: string | null;
  createdAt: string | null;
  quantityAffected: number | null;
}

export interface PlantingHarvestConflict {
  code: string;
  severity: 'warning' | 'conflict';
  eventId: string | null;
  eventDate: string | null;
  message: string;
  data: Record<string, unknown>;
}

export interface PlantingHarvestEvent {
  id: string;
  type: string;
  templateCode: string | null;
  eventDate: string | null;
  createdAt: string | null;
  quantityAffected: number | null;
  quantityKg: number | null;
  quantityKgSource: 'recorded' | 'derived' | null;
  quantityKgOrigin: HarvestQuantityOrigin;
  quantityKgReplayPolicy: HarvestQuantityReplayPolicy;
  quantityKgEventProperty: number | null;
  quantityKgRecord: number | null;
  projectionStatus: 'synced' | 'mismatch' | 'projection_only' | 'event_property' | null;
  // Phase 8 storage-chain: materialized into a product lot, and whether downstream
  // withdrawals/reservations lock it against rejection.
  inventoryMaterialized?: boolean;
  inventoryLocked?: boolean;
  inventoryDependencies?: {
    materialized: boolean;
    lotId: string | null;
    depositedKg: number;
    onHand: number;
    reserved: number;
    withdrawnKg: number;
    locked: boolean;
    reasons: string[];
  } | null;
  records: PlantingHarvestRecord[];
  conflicts: PlantingHarvestConflict[];
  notes: string | null;
  provenance: {
    sourceType: string;
    sourceRef: string | null;
    sourceConfidence: number | null;
    decisionState: string;
    quantityAffectedConfidence: number | null;
    quantityKgConfidence: number | null;
    quantityAffectedOrigin: HarvestQuantityOrigin;
    quantityAffectedReplayPolicy: HarvestQuantityReplayPolicy;
    quantityAffectedCanRecalculate: boolean;
    quantityKgOrigin: HarvestQuantityOrigin;
    quantityKgReplayPolicy: HarvestQuantityReplayPolicy;
    quantityKgCanRecalculate: boolean;
  };
}

export interface PlantingHarvestInfo {
  plantingId: string;
  plantingType: 'single' | 'batch';
  lifecycleStatus: string;
  harvestMode: 'whole_plant' | 'fruiting' | 'leaf_cut' | 'unknown';
  completeLifecycleOnHarvest: boolean;
  currentQuantity: number;
  initialQuantity: number;
  expectedYieldPerPlantKg: number | null;
  totalHarvestedUnits: number;
  totalHarvestedKg: number | null;
  totalHarvestedKgSource: 'recorded' | 'derived' | 'mixed' | null;
  harvestEventCount: number;
  harvestReplay: {
    hasReplayableValues: boolean;
    replayableEventCount: number;
    hardFactEventCount: number;
  };
  terminalDate: string | null;
  harvestConflicts: PlantingHarvestConflict[];
  harvestConflictCount: number;
  hasHarvestConflicts: boolean;
  harvestEvents: PlantingHarvestEvent[];
  harvestRecords: PlantingHarvestRecord[];
  removalEvents: PlantingRemovalEvent[];
}

export type HarvestConflictResolutionAction =
  | 'sync_projection_from_event'
  | 'set_quantity_affected'
  | 'reduce_quantity_affected_to_remaining'
  | 'reject_harvest_event';

export interface ResolveHarvestConflictPayload {
  conflictCode: string;
  action: HarvestConflictResolutionAction;
  eventId?: string | null;
  quantityAffected?: number | null;
}

export interface ResolveHarvestConflictResult {
  plantingId: string;
  conflictCode: string;
  action: HarvestConflictResolutionAction;
  resolved: boolean;
  deletedCount?: number;
  quantityKg?: number;
  quantityAffected?: number;
  rejectedEventId?: string;
  projectionSync?: unknown;
}

const toKey = (value: string | number | null | undefined): string => (
  value == null ? '' : String(value)
);

/** Runs in the query transform; a language switch resets the RTK caches, so it reruns. */
const currentPropertiesFromRuntime = (runtime?: WorldRuntimePlanting) => {
  const props = [];
  if (typeof runtime?.healthScore === 'number') {
    props.push({
      propertyId: 'health_score',
      propertyCode: 'health_score',
      propertyName: i18n.t('plantings:runtimeProperty.healthScore'),
      dataType: 'number',
      valueNumber: runtime.healthScore,
      unit: '%',
    });
  }
  if (typeof runtime?.moisturePct === 'number') {
    props.push({
      propertyId: 'moisture_pct',
      propertyCode: 'moisture_pct',
      propertyName: i18n.t('plantings:runtimeProperty.moisture'),
      dataType: 'number',
      valueNumber: runtime.moisturePct,
      unit: '%',
    });
  }
  if (runtime?.vegetationStage) {
    props.push({
      propertyId: 'vegetation_stage',
      propertyCode: 'vegetation_stage',
      propertyName: i18n.t('plantings:runtimeProperty.stage'),
      dataType: 'text',
      valueText: runtime.vegetationStage,
    });
  }
  if (runtime?.lifecyclePhase) {
    props.push({
      propertyId: 'lifecycle_phase',
      propertyCode: 'lifecycle_phase',
      propertyName: i18n.t('plantings:runtimeProperty.lifecyclePhase'),
      dataType: 'text',
      valueText: runtime.lifecyclePhase,
    });
  }
  if (typeof runtime?.lifecycleGateProgressPct === 'number') {
    props.push({
      propertyId: 'lifecycle_gate_progress_pct',
      propertyCode: 'lifecycle_gate_progress_pct',
      propertyName: i18n.t('plantings:runtimeProperty.nextPhaseProgress'),
      dataType: 'number',
      valueNumber: runtime.lifecycleGateProgressPct,
      unit: '%',
    });
  }
  if (runtime?.lifecycleNextPhase) {
    props.push({
      propertyId: 'lifecycle_next_phase',
      propertyCode: 'lifecycle_next_phase',
      propertyName: i18n.t('plantings:runtimeProperty.nextPhase'),
      dataType: 'text',
      valueText: runtime.lifecycleNextPhase,
    });
  }
  const bbch = adaptBbchInfo(runtime);
  if (bbch?.code != null) {
    props.push({
      propertyId: 'bbch_code',
      propertyCode: 'bbch_code',
      propertyName: 'BBCH',
      dataType: 'number',
      valueNumber: bbch.code,
    });
  }
  return props;
};

const resolveWorldLifecycleStatus = (
  planting: WorldPlanting,
  runtime?: WorldRuntimePlanting
): PlantingLifecycleStatus => {
  const structuralStatus = String(planting.lifecycleStatus || 'active').toLowerCase();
  const runtimeStatus = String(runtime?.lifecycleStatus || '').toLowerCase();
  const vegetationStage = String(runtime?.vegetationStage || '').toLowerCase();
  const lifecyclePhase = String(runtime?.lifecyclePhase || '').toLowerCase();

  // Durable terminal flows such as harvest/transfer/sale must not collapse into
  // "dead" because a replay still has low health or stale runtime flags.
  if (['archived', 'sold', 'transferred', 'holding'].includes(structuralStatus)) {
    return structuralStatus as PlantingLifecycleStatus;
  }
  if (structuralStatus === 'dead') {
    return 'dead';
  }

  if (runtime?.isDead || runtimeStatus === 'dead' || vegetationStage === 'dead' || lifecyclePhase === 'dead') {
    return 'dead';
  }
  if (['archived', 'sold', 'transferred', 'holding'].includes(runtimeStatus)) {
    return runtimeStatus as PlantingLifecycleStatus;
  }
  if (runtimeStatus === 'active' || runtimeStatus === 'planned') {
    return runtimeStatus as PlantingLifecycleStatus;
  }

  return (structuralStatus || 'active') as PlantingLifecycleStatus;
};

const buildPlantingFromWorld = (world: SaveWorld, planting: WorldPlanting): Planting => {
  const structure = world.structure;
  const runtime = world.runtime?.plantings?.[String(planting.id)];
  const bed = planting.bedId ? structure.beds.find((item) => String(item.id) === String(planting.bedId)) : null;
  const plotId = planting.plotId || bed?.plotId || null;
  const plot = plotId ? structure.plots.find((item) => String(item.id) === String(plotId)) : null;
  const fieldId = planting.fieldId || plot?.fieldId || null;
  const field = fieldId ? structure.fields.find((item) => String(item.id) === String(fieldId)) : null;

  return adaptPlanting({
    id: planting.id,
    crop: {
      id: planting.cropId || planting.id,
      name: planting.crop || i18n.t('plantings:add.crop'),
      latinName: '',
    },
    plantingType: planting.quantity && planting.quantity > 1 ? 'batch' : planting.plantingType || 'single',
    quantity: planting.quantity ?? 1,
    initialQuantity: planting.initialQuantity ?? null,
    lifecycleStatus: resolveWorldLifecycleStatus(planting, runtime),
    originDate: planting.originDate ?? null,
    autoHarvest: planting.autoHarvest === true,
    inStorage: planting.inStorage === true,
    locations: planting.bedId ? [{
      id: planting.bedId,
      bedId: planting.bedId,
      fromDate: planting.originDate ?? null,
      toDate: null,
      bed: bed ? { id: bed.id, name: bed.name, plot_id: bed.plotId } : null,
      plot: plot ? { id: plot.id, name: plot.name, field_id: plot.fieldId } : null,
      field: field ? { id: field.id, name: field.name, farm_id: field.farmId } : null,
      farm: world.farm.id ? { id: world.farm.id, name: world.farm.name } : null,
    }] : [],
    locationContext: {
      farmsTotal: world.farm.id ? 1 : 0,
      fieldsTotal: structure.fields.length,
      plotsTotal: structure.plots.length,
    },
    bbch: adaptBbchInfo(runtime),
    currentProperties: currentPropertiesFromRuntime(runtime),
  });
};

const plantingsFromWorld = (world: SaveWorld, params?: PlantingQueryParams): Planting[] => {
  const items = (world.structure?.plantings || []).filter((planting) => {
    if (params?.fieldId && toKey(planting.fieldId) !== toKey(params.fieldId)) return false;
    if (params?.plotId && toKey(planting.plotId) !== toKey(params.plotId)) {
      const bed = planting.bedId
        ? world.structure.beds.find((item) => toKey(item.id) === toKey(planting.bedId))
        : null;
      if (toKey(bed?.plotId) !== toKey(params.plotId)) return false;
    }
    if (params?.bedId) {
      const currentMatch = toKey(planting.bedId) === toKey(params.bedId);
      const historicalMatch = !params.lifecycleStatus
        && Array.isArray(planting.historicalBedIds)
        && planting.historicalBedIds.some((id) => toKey(id) === toKey(params.bedId));
      if (!currentMatch && !historicalMatch) return false;
    }
    if (params?.cropId && toKey(planting.cropId) !== toKey(params.cropId)) return false;
    const runtime = world.runtime?.plantings?.[String(planting.id)];
    const lifecycleStatus = resolveWorldLifecycleStatus(planting, runtime);
    if (params?.lifecycleStatus && lifecycleStatus !== params.lifecycleStatus) return false;
    return true;
  });
  return items.map((planting) => buildPlantingFromWorld(world, planting));
};

export const plantingsApi = apiSlice
  .enhanceEndpoints({ addTagTypes: ['SimTimeseries'] })
  .injectEndpoints({
    endpoints: (build) => ({
    getPlantings: build.query<Planting[], PlantingQueryParams | undefined>({
      queryFn: async (params, api) => {
        const saveId = params?.saveId;
        if (!saveId) {
          return { error: { status: 400, data: 'saveId is required for getPlantings' } };
        }
        try {
          // "All / historical" mode (no lifecycleStatus filter) must surface terminal
          // plantings — a whole-plant harvest archives the planting and CLOSES its bed
          // location, so it drops out of the date-windowed current-state snapshot.
          // Load the unwindowed snapshot (same one getPlantingById uses) so the
          // plantingsFromWorld historicalBedIds match can find harvested/archived
          // plantings, e.g. the bed page "Показаны все посадки" toggle. With a
          // lifecycleStatus filter we keep the lean windowed snapshot.
          const includeHistorical = !params?.lifecycleStatus;
          const world = await loadSaveWorldSnapshot(
            api,
            saveId,
            includeHistorical ? { includeFuturePlantings: true } : undefined,
          );
          return { data: plantingsFromWorld(world, params) };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, params) =>
        result
          ? [
              ...result.map((planting) => ({ type: 'Planting' as const, id: String(planting.id) })),
              { type: 'Planting' as const, id: 'LIST' },
              ...(params?.saveId ? [{ type: 'SaveV2' as const, id: `world-${params.saveId}` }] : []),
            ]
          : [{ type: 'Planting' as const, id: 'LIST' }],
    }),
    getPlantingById: build.query<Planting, { saveId: string; id: string | number; simSessionId?: string | null }>({
      queryFn: async ({ saveId, id }, api) => {
        try {
          // Management variant: include not-yet-planted records so the detail page
          // can still view/manage a planting whose date is after the sim cursor.
          const world = await loadSaveWorldSnapshot(api, saveId, { includeFuturePlantings: true });
          const planting = world.structure.plantings.find((item) => String(item.id) === String(id));
          if (!planting) {
            return { error: { status: 404, data: 'Planting not found in world snapshot' } };
          }
          return { data: buildPlantingFromWorld(world, planting) };
        } catch (error) {
          return { error: toQueryFnError(error) };
        }
      },
      providesTags: (result, error, { saveId, id }) => [
        { type: 'Planting' as const, id: String(id) },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),
    createPlanting: build.mutation<Planting, PlantingPayload & { saveId: string }>({
      query: ({ saveId, ...body }) => ({ url: `/saves/${saveId}/plantings`, method: 'POST', body }),
      transformResponse: (response: any) => adaptPlanting(response?.planting ?? response),
      invalidatesTags: (_r, _e, { saveId }) => [
        { type: 'Planting', id: 'LIST' },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
        { type: 'SimTimeseries' as any },
      ],
    }),
    updatePlanting: build.mutation<Planting, { saveId: string; id: string | number; payload: PlantingUpdatePayload }>({
      query: ({ saveId, id, payload }) => ({ url: `/saves/${saveId}/plantings/${id}`, method: 'PATCH', body: payload }),
      transformResponse: (response: any) => adaptPlanting(response?.planting ?? response),
      invalidatesTags: (result, error, { saveId, id }) => [
        { type: 'Planting' as const, id: String(id) },
        { type: 'Planting' as const, id: 'LIST' },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
        { type: 'SimTimeseries' as any },
      ],
    }),
    applyPlantingDisposition: build.mutation<
      PlantingDispositionResult,
      { saveId: string; id: string | number; payload: PlantingDispositionPayload }
    >({
      query: ({ saveId, id, payload }) => ({ url: `/saves/${saveId}/plantings/${id}/disposition`, method: 'POST', body: payload }),
      invalidatesTags: (result, error, { saveId, id }) => [
        { type: 'Planting' as const, id: String(id) },
        { type: 'Planting' as const, id: 'LIST' },
        { type: 'PlantingEvent' as const, id: String(id) },
        { type: 'PlantingAttribute' as const, id: String(id) },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
        { type: 'SimTimeseries' as any },
      ],
    }),
    uploadPlantingPhoto: build.mutation<
      { photo: any; url: string },
      { saveId: string; plantingId: string | number; file: File; description?: string }
    >({
      query: ({ saveId, plantingId, file, description }) => {
        const formData = new FormData();
        formData.append('photo', file);
        if (description) {
          formData.append('description', description);
        }
        return {
          url: `/saves/${saveId}/plantings/${plantingId}/photos`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { plantingId }) => [
        { type: 'Planting' as const, id: String(plantingId) },
      ],
    }),
    deletePlantingPhoto: build.mutation<void, { saveId: string; plantingId: string | number; photoId: string | number }>({
      query: ({ saveId, plantingId, photoId }) => ({
        url: `/saves/${saveId}/plantings/${plantingId}/photos/${photoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { plantingId }) => [
        { type: 'Planting' as const, id: String(plantingId) },
      ],
    }),

    getPlantingHarvestInfo: build.query<PlantingHarvestInfo, { plantingId: string }>({
      query: ({ plantingId }) => `/plantings/${plantingId}/harvest-info`,
      providesTags: (_r, _e, { plantingId }) => [
        { type: 'Planting' as const, id: String(plantingId) },
      ],
    }),

    resolvePlantingHarvestConflict: build.mutation<
      ResolveHarvestConflictResult,
      { plantingId: string; payload: ResolveHarvestConflictPayload }
    >({
      query: ({ plantingId, payload }) => ({
        url: `/plantings/${plantingId}/harvest-conflicts/resolve`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_r, _e, { plantingId }) => [
        { type: 'Planting' as const, id: String(plantingId) },
        { type: 'PlantingEvent' as const, id: String(plantingId) },
      ],
    }),

    unarchivePlanting: build.mutation<
      { success: true; plantingId: string; lifecycleStatus: string },
      { plantingId: string }
    >({
      query: ({ plantingId }) => ({
        url: `/plantings/${plantingId}/unarchive`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { plantingId }) => [
        { type: 'Planting' as const, id: String(plantingId) },
        { type: 'Planting' as const, id: 'LIST' },
      ],
    }),
    }),
  });

export const {
  useGetPlantingsQuery,
  useGetPlantingByIdQuery,
  useCreatePlantingMutation,
  useUpdatePlantingMutation,
  useApplyPlantingDispositionMutation,
  useUploadPlantingPhotoMutation,
  useDeletePlantingPhotoMutation,
  useGetPlantingHarvestInfoQuery,
  useResolvePlantingHarvestConflictMutation,
  useUnarchivePlantingMutation,
} = plantingsApi;
